import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { X, Sparkles, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';

const PLACEHOLDER = 'Select an option';
const CUSTOM = 'Use my own wording';
const PRIORITIES = [
  'Reduce cost', 'Increase revenue', 'Improve cash flow', 'Improve customer experience',
  'Improve productivity', 'Reduce risk / compliance issues', 'Improve quality', 'Expand market access',
];

export default function GoalDrilldownModal({ goalIndex, goal, mode = 'refine', onClose, onKeepAsIs, onComplete }) {
  const isDataOnly = mode === 'data-only';
  const [stage, setStage] = useState(isDataOnly ? 'data' : 'consent'); // consent | questions | statement | data
  const [suggestions, setSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [action, setAction] = useState(PLACEHOLDER);
  const [actionCustom, setActionCustom] = useState('');
  const [approach, setApproach] = useState(PLACEHOLDER);
  const [approachCustom, setApproachCustom] = useState('');
  const [kpi, setKpi] = useState(PLACEHOLDER);
  const [kpiCustom, setKpiCustom] = useState('');
  const [priority, setPriority] = useState(PRIORITIES[0]);
  const [refinedGoal, setRefinedGoal] = useState('');
  const [dataReqs, setDataReqs] = useState({ internal: [], external: [] });
  const [internalAvail, setInternalAvail] = useState({});
  const [externalAvail, setExternalAvail] = useState({});
  const [externalSrc, setExternalSrc] = useState({});
  const [loadingData, setLoadingData] = useState(isDataOnly);
  const [formError, setFormError] = useState('');
  const [dataChoiceError, setDataChoiceError] = useState(false);

  const goalText = goal['Goal Description'] || '';
  const challengeText = goal['Challenges'] || '';

  useEffect(() => {
    if (stage === 'questions' && !suggestions) {
      setLoadingSuggestions(true);
      api.post('/llm/drilldown-suggestions', { goal: goalText, challenge: challengeText })
        .then(r => setSuggestions(r.data))
        .catch(() => setSuggestions({ actions: [], approaches: [], kpis: [] }))
        .finally(() => setLoadingSuggestions(false));
    }
  }, [stage]); // eslint-disable-line

  useEffect(() => {
    if (!isDataOnly) return undefined;
    let cancelled = false;
    setStage('data');
    setRefinedGoal(goalText);
    setLoadingData(true);
    setDataChoiceError(false);
    api.post('/llm/data-requirements', {
      goal: goalText,
      challenge: challengeText,
      intended_action: goalText,
    })
      .then(r => {
        if (cancelled) return;
        const requirements = r.data || { internal: [], external: [] };
        setDataReqs(requirements);
        const initAvail = {}; (requirements.internal || []).forEach((_, i) => initAvail[i] = 'Yes'); setInternalAvail(initAvail);
        const initExternalAvail = {}; (requirements.external || []).forEach((_, i) => initExternalAvail[i] = 'No'); setExternalAvail(initExternalAvail);
        const initSrc = {}; (requirements.external || []).forEach((it, i) => initSrc[i] = it.suggested_source || ''); setExternalSrc(initSrc);
      })
      .catch(() => {
        if (!cancelled) setDataReqs({ internal: [], external: [] });
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false);
      });
    return () => { cancelled = true; };
  }, [isDataOnly, goalText, challengeText]);

  const valOf = (sel, custom) => sel === CUSTOM ? custom : (sel === PLACEHOLDER ? '' : sel);
  const resolvedAction = valOf(action, actionCustom);
  const resolvedApproach = valOf(approach, approachCustom);
  const resolvedKpi = valOf(kpi, kpiCustom);
  const internalItems = dataReqs.internal || [];
  const allInternalUnavailable = internalItems.length > 0 && internalItems.every((_, i) => (
    String(internalAvail[i] || 'Yes').trim().toLowerCase() === 'no'
  ));

  const isWeakAnswer = (value = '') => {
    const text = value.trim().toLowerCase();
    return !text || text.length < 8 || ['no idea', 'no clue', "don't know", 'dont know', 'you tell me', 'n/a', 'na'].includes(text);
  };

  const inferAICapabilities = () => {
    const text = `${goalText} ${challengeText} ${resolvedAction} ${resolvedApproach}`.toLowerCase();
    const caps = [];
    if (/(forecast|predict|demand|risk|likelihood)/.test(text)) caps.push('Prediction');
    if (/(optimize|cost|profit|margin|schedule|inventory)/.test(text)) caps.push('Optimization');
    if (/(manual|automate|workflow|process|follow)/.test(text)) caps.push('Automation');
    if (/(text|ticket|email|feedback|document|query)/.test(text)) caps.push('NLP');
    if (/(defect|anomaly|fraud|variance)/.test(text)) caps.push('Anomaly Detection');
    if (/(image|visual|inspection|photo)/.test(text)) caps.push('Computer Vision');
    return caps.length ? caps : ['Prediction'];
  };

  const proceedToStatement = async () => {
    const insufficient = [
      ['intended action', resolvedAction],
      ['approach', resolvedApproach],
      ['success KPI', resolvedKpi],
    ].filter(([, value]) => isWeakAnswer(value)).map(([label]) => label);
    if (insufficient.length) {
      setFormError(`Please choose a suggested option or enter a more specific answer for: ${insufficient.join(', ')}.`);
      return;
    }

    setFormError('');
    const caps = inferAICapabilities();
    const capPhrase = caps.join(', ').toLowerCase();
    const refined = `Use an AI-assisted solution with ${capPhrase} on relevant business data to ${(resolvedAction || 'achieve the business outcome').replace(/\.$/, '').toLowerCase()}. The solution should ${(resolvedApproach || 'use available business data').replace(/\.$/, '').toLowerCase()}. It should address: ${challengeText.replace(/\.$/, '')}. This supports the business priority: ${priority}. Success will be measured through ${(resolvedKpi || 'measurable improvements').replace(/\.$/, '')}.`;
    setRefinedGoal(refined);
    setLoadingData(true);
    try {
      const r = await api.post('/llm/data-requirements', { goal: refined, challenge: challengeText, intended_action: resolvedAction });
      setDataReqs(r.data);
      const initAvail = {}; (r.data.internal || []).forEach((_, i) => initAvail[i] = 'Yes'); setInternalAvail(initAvail);
      const initExternalAvail = {}; (r.data.external || []).forEach((_, i) => initExternalAvail[i] = 'No'); setExternalAvail(initExternalAvail);
      const initSrc = {}; (r.data.external || []).forEach((it, i) => initSrc[i] = it.suggested_source || ''); setExternalSrc(initSrc);
    } catch { setDataReqs({ internal: [], external: [] }); }
    finally { setLoadingData(false); setStage('statement'); }
  };

  const complete = () => {
    if (allInternalUnavailable) {
      setDataChoiceError(true);
      return;
    }

    const caps = inferAICapabilities();
    const internalLines = internalItems.map((it, i) => `${it} | Available: ${internalAvail[i] || 'Yes'}`).join('\n');
    const externalLines = (dataReqs.external || [])
      .map((it, i) => ({ it, i }))
      .filter(({ i }) => String(externalAvail[i] || 'No').trim().toLowerCase() === 'yes')
      .map(({ it, i }) => `${it.item || it} | Source: ${externalSrc[i] || 'Not specified'}`)
      .join('\n');
    onComplete({
      data_only: isDataOnly,
      refined_goal: isDataOnly ? goalText : refinedGoal,
      ai_capabilities: caps.join(', '),
      kpi: resolvedKpi,
      priority,
      internal: internalLines,
      external: externalLines,
    });
  };

  const closeAllowed = !allInternalUnavailable;
  const requestClose = () => {
    if (closeAllowed) onClose();
  };
  const focusFirstInternalDataChoice = () => {
    setDataChoiceError(false);
    document.querySelector('[data-testid="dd-internal-0"]')?.focus();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/40 backdrop-blur-sm animate-reveal" data-testid="drilldown-modal" onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
        <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-[#002FA7]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{isDataOnly ? 'AI Data Requirements' : 'AI Goal Drilldown'}</div>
              <div className="font-display font-bold text-zinc-950">Goal #{goalIndex + 1}</div>
            </div>
          </div>
          <button onClick={requestClose} disabled={!closeAllowed} className="text-zinc-500 hover:text-zinc-950 disabled:cursor-not-allowed disabled:opacity-40 p-1" data-testid="drilldown-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          {stage === 'consent' && (
            <div data-testid="drilldown-consent">
              <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 mb-5">
                This looks like a strategic goal. Let's break it down into something that AI can act on. AI cannot beat human strategy — but we can help operationalize it.
              </div>
              <div className="space-y-3 text-sm">
                <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Goal:</span><div className="text-zinc-900 mt-1">{goalText}</div></div>
                <div><span className="text-zinc-500 text-xs uppercase tracking-wider">Challenge:</span><div className="text-zinc-900 mt-1">{challengeText}</div></div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={onKeepAsIs || onClose} className="flex-1 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50" data-testid="drilldown-keep">Keep as-is</button>
                <button onClick={() => setStage('questions')} className="flex-1 bg-[#002FA7] text-white px-4 py-2.5 text-sm hover:bg-[#00227A] flex items-center justify-center gap-2" data-testid="drilldown-start">
                  Yes, drill down <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {stage === 'questions' && (
            <div data-testid="drilldown-questions">
              {loadingSuggestions ? (
                <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-3"><Loader2 className="w-5 h-5 animate-spin" /> AI is preparing tailored options…</div>
              ) : (
                <div className="space-y-4">
                  <DDSelect label="What do you intend to do to achieve this goal?" options={suggestions?.actions || []} value={action} setValue={setAction} custom={actionCustom} setCustom={setActionCustom} testid="dd-action" />
                  <DDSelect label="How do you intend to achieve it?" options={suggestions?.approaches || []} value={approach} setValue={setApproach} custom={approachCustom} setCustom={setApproachCustom} testid="dd-approach" />
                  <div>
                    <span className="text-xs font-medium text-zinc-700 mb-1.5 block uppercase tracking-wider">Which business priority does this support?</span>
                    <select value={priority} onChange={e => setPriority(e.target.value)} className="w-full bg-zinc-50 border border-zinc-200 px-3.5 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none" data-testid="dd-priority">
                      {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <DDSelect label="What desired outcome or KPI will prove success?" options={suggestions?.kpis || []} value={kpi} setValue={setKpi} custom={kpiCustom} setCustom={setKpiCustom} testid="dd-kpi" />
                  {formError && (
                    <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                      {formError}
                    </div>
                  )}
                  <div className="flex gap-3 pt-3">
                    <button onClick={() => setStage('consent')} className="flex-1 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50 flex items-center justify-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
                    <button onClick={proceedToStatement} disabled={!resolvedAction || !resolvedApproach || !resolvedKpi || loadingData} className="flex-1 bg-[#002FA7] text-white px-4 py-2.5 text-sm hover:bg-[#00227A] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="dd-identify">
                      {loadingData ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Identify Goal <ArrowRight className="w-4 h-4" /></>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {stage === 'statement' && (
            <div data-testid="drilldown-statement">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Goal Statement</div>
              <textarea rows={6} className="w-full bg-zinc-50 border border-zinc-200 px-3.5 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none" value={refinedGoal} onChange={e => setRefinedGoal(e.target.value)} data-testid="dd-refined" />
              <div className="flex gap-3 mt-4">
                <button onClick={() => setStage('questions')} className="flex-1 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50">← Back</button>
                <button onClick={() => setStage('data')} className="flex-1 bg-[#002FA7] text-white px-4 py-2.5 text-sm hover:bg-[#00227A]" data-testid="dd-accept-goal">Accept goal →</button>
              </div>
            </div>
          )}

          {stage === 'data' && (
            <div data-testid="drilldown-data">
              {loadingData ? (
                <div className="py-16 text-center text-zinc-500 flex items-center justify-center gap-3" data-testid="dd-data-loading">
                  <Loader2 className="w-5 h-5 animate-spin" /> AI is identifying the data needed for this goal…
                </div>
              ) : (dataReqs.internal || []).length === 0 && (dataReqs.external || []).length === 0 ? (
                <>
                  <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 mb-5" data-testid="dd-no-data-identified">
                    <div className="font-semibold mb-1">We could not identify reliable data requirements for this goal.</div>
                    <p>You can keep the goal as-is and choose data availability yourself, or go back and refine the goal statement.</p>
                  </div>
                  <div className="flex gap-3 mt-5">
                    <button onClick={isDataOnly ? onClose : () => setStage('statement')} className="flex-1 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50">{isDataOnly ? 'Close' : '← Back'}</button>
                    <button onClick={onKeepAsIs || onClose} className="flex-1 bg-[#002FA7] text-white px-4 py-2.5 text-sm hover:bg-[#00227A]" data-testid="dd-keep-no-data-identified">
                      Keep As Is
                    </button>
                  </div>
                </>
              ) : (
                <>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Internal Data</div>
              <div className="space-y-2 mb-5">
                {dataReqs.internal?.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 bg-zinc-50 border border-zinc-200 p-3 text-sm">
                    <div className="flex-1">{it}</div>
                    <select value={internalAvail[i] || 'Yes'} onChange={e => { setInternalAvail({ ...internalAvail, [i]: e.target.value }); setDataChoiceError(false); }} className="bg-white border border-zinc-200 px-2 py-1 text-xs" data-testid={`dd-internal-${i}`}>
                      <option>Yes</option><option>No</option>
                    </select>
                  </div>
                ))}
              </div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">External Data</div>
              {(dataReqs.external || []).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-950 mb-3">
                  The external data below may be relevant. Select Yes only when you already have that data available; otherwise leave it as No and add it later when you can provide it.
                </div>
              )}
              <div className="space-y-2">
                {dataReqs.external?.map((it, i) => (
                  <div key={i} className="grid gap-3 bg-zinc-50 border border-zinc-200 p-3 text-sm md:grid-cols-[1fr_6rem_1fr]">
                    <div>{it.item || it}</div>
                    <select value={externalAvail[i] || 'No'} onChange={e => setExternalAvail({ ...externalAvail, [i]: e.target.value })} className="bg-white border border-zinc-200 px-2 py-1 text-xs" data-testid={`dd-external-avail-${i}`}>
                      <option>No</option><option>Yes</option>
                    </select>
                    <input value={externalSrc[i] || ''} onChange={e => setExternalSrc({ ...externalSrc, [i]: e.target.value })} className="bg-white border border-zinc-200 px-2 py-1 text-xs" placeholder="Data source" data-testid={`dd-external-${i}`} />
                  </div>
                ))}
              </div>
              {(dataChoiceError || allInternalUnavailable) && (
                <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 mt-5" data-testid="dd-no-data-warning">
                  <div className="font-semibold mb-1">At least one internal data source needs to be selected.</div>
                  <p className="mb-4">If you do not have any internal data, please select Keep As Is.</p>
                  <div className="flex gap-3">
                    <button onClick={focusFirstInternalDataChoice} className="flex-1 bg-[#002FA7] text-white px-4 py-2.5 text-sm hover:bg-[#00227A]" data-testid="dd-select-data">
                      Select Data
                    </button>
                    <button onClick={onKeepAsIs || onClose} className="flex-1 border border-amber-300 bg-white px-4 py-2.5 text-sm hover:bg-amber-100" data-testid="dd-keep-no-data">
                      Keep As Is
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-3 mt-5">
                {!isDataOnly && <button onClick={() => setStage('statement')} className="flex-1 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50">← Back</button>}
                <button onClick={complete} disabled={dataChoiceError} className="flex-1 bg-[#002FA7] text-white px-4 py-2.5 text-sm hover:bg-[#00227A] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="dd-done">
                  Done <ArrowRight className="w-4 h-4" />
                </button>
              </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DDSelect({ label, options, value, setValue, custom, setCustom, testid }) {
  const allOpts = [PLACEHOLDER, ...options, CUSTOM];
  return (
    <div>
      <span className="text-xs font-medium text-zinc-700 mb-1.5 block uppercase tracking-wider">{label}</span>
      <select value={value} onChange={e => setValue(e.target.value)} className="w-full bg-zinc-50 border border-zinc-200 px-3.5 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none" data-testid={testid}>
        {allOpts.map((o, i) => <option key={i}>{o}</option>)}
      </select>
      {value === CUSTOM && (
        <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="Type your custom answer" className="mt-2 w-full bg-zinc-50 border border-zinc-200 px-3.5 py-2.5 text-sm focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none" data-testid={`${testid}-custom`} />
      )}
    </div>
  );
}
